import { Injectable } from '@angular/core';
import { getDocs } from 'firebase/firestore';

import { ownedCollection } from '../../../utils/firestore-ownership';
import { round2 } from '../../finances/finance-summary.service';
import { Month, monthOf } from '../../shared/month/month.service';

export type InvestmentClass = 'STOCK' | 'REIT' | 'CRYPTO' | 'FIXED';

export const INVESTMENT_CLASSES: { value: InvestmentClass, label: string, color: string }[] = [
  // Chart colors of the theme (theme.scss)
  { value: 'STOCK', label: 'Stocks', color: 'var(--app-c4)' },
  { value: 'REIT', label: 'Reits', color: 'var(--app-c3)' },
  { value: 'CRYPTO', label: 'Cryptos', color: 'var(--app-c2)' },
  { value: 'FIXED', label: 'Fixed incomes', color: 'var(--app-c1)' }
];

// Stocks, Reits, Cryptos and FixedIncomes records in one shape
export interface InvestmentOperation {
  type: InvestmentClass,
  date: Date,
  share: string,
  side: 'BUY' | 'SELL',
  amount: number,
  price: number,
  tax: number,
  total: number,
  note?: string,
  usdBrl?: number | null,
  btcUsd?: number | null,
  profitability?: number,
  maturity?: Date | null
}

export interface DividendPayment {
  date: Date,
  share: string,
  type: string,
  amount: number,
  value: number,
  total: number
}

export interface ClassSummary {
  type: InvestmentClass,
  label: string,
  color: string,
  operations: InvestmentOperation[],
  buys: number,
  sells: number
}

export interface InvestmentMonthSummary {
  classes: ClassSummary[],
  dividends: DividendPayment[],
  totals: { buys: number, sells: number, dividends: number, net: number },
  counts: { buys: number, sells: number, buyClasses: number },
  // Balance of the same month in Finances, null when it could not be read
  balance: number | null
}

export interface InvestmentRecords {
  stocks: any[],
  reits: any[],
  cryptos: any[],
  fixedIncomes: any[],
  dividends: any[],
  shares: any[]
}

@Injectable({
  providedIn: 'root'
})
export class InvestmentsSummaryService {

  // Few records per user: everything is read once and each month is computed with summarizeMonth
  async load(): Promise<InvestmentRecords> {
    const [stocks, reits, cryptos, fixedIncomes, dividends, shares] = await Promise.all([
      this.list('Stocks'),
      this.list('Reits'),
      this.list('Cryptos'),
      this.list('FixedIncomes'),
      this.list('Dividends'),
      this.list('Shares')
    ]);
    return { stocks, reits, cryptos, fixedIncomes, dividends, shares };
  }

  // No composite index needed: the month is filtered here
  private async list(name: string): Promise<any[]> {
    const docs = await getDocs(ownedCollection(name));
    return docs.docs.map(d => ({ ...d.data(), id: d.id }));
  }
}

export function summarizeMonth(month: Month, records: InvestmentRecords, balance: number | null): InvestmentMonthSummary {
  const inMonth = (date: Date | null) => !!date && monthOf(date) == month;

  const operations: InvestmentOperation[] = [
    ...records.stocks.map(r => trade('STOCK', r)),
    ...records.reits.map(r => trade('REIT', r)),
    ...records.cryptos.map(r => ({ ...trade('CRYPTO', r), usdBrl: r.usdBrl ?? null, btcUsd: r.btcUsd ?? null })),
    ...records.fixedIncomes.map(fixed)
  ].filter(o => inMonth(o.date)).sort((a, b) => a.date.getTime() - b.date.getTime());

  const classes = INVESTMENT_CLASSES
    .map(c => {
      const items = operations.filter(o => o.type == c.value);
      return {
        type: c.value, label: c.label, color: c.color, operations: items,
        buys: sumOf(items.filter(o => o.side == 'BUY')),
        sells: sumOf(items.filter(o => o.side == 'SELL'))
      };
    })
    .filter(c => c.operations.length);

  const shareTypes = new Map(records.shares.map(s => [s.code, s.type as string]));
  const dividends: DividendPayment[] = records.dividends
    .map(d => ({
      date: toDate(d.date)!,
      share: d.share,
      type: shareTypes.get(d.share) ?? '',
      amount: Number(d.amount) || 0,
      value: Number(d.value) || 0,
      total: Number(d.total) || 0
    }))
    .filter(d => inMonth(d.date))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const buys = round2(classes.reduce((total, c) => total + c.buys, 0));
  const sells = round2(classes.reduce((total, c) => total + c.sells, 0));

  return {
    classes,
    dividends,
    totals: { buys, sells, dividends: sumOf(dividends), net: round2(buys - sells) },
    counts: {
      buys: operations.filter(o => o.side == 'BUY').length,
      sells: operations.filter(o => o.side == 'SELL').length,
      buyClasses: classes.filter(c => c.buys).length
    },
    balance
  };
}

// Stocks, Reits and Cryptos: total already includes the tax where there is one
function trade(type: InvestmentClass, r: any): InvestmentOperation {
  return {
    type,
    date: toDate(r.date)!,
    share: r.share,
    side: r.side == 'SELL' ? 'SELL' : 'BUY',
    amount: Number(r.amount) || 0,
    price: Number(r.price) || 0,
    tax: Number(r.tax) || 0,
    total: Number(r.total) || 0,
    note: r.note
  };
}

// Fixed incomes are applications: the buy date and the invested value
function fixed(r: any): InvestmentOperation {
  return {
    type: 'FIXED',
    date: toDate(r.buy)!,
    share: r.share,
    side: 'BUY',
    amount: 1,
    price: Number(r.invested) || 0,
    tax: 0,
    total: Number(r.invested) || 0,
    profitability: Number(r.profitability) || 0,
    maturity: toDate(r.maturity)
  };
}

function sumOf(items: { total: number }[]): number {
  return round2(items.reduce((total, i) => total + i.total, 0));
}

// Firestore Timestamp, { seconds }, Date or string
export function toDate(value: any): Date | null {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds != undefined) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}
